const ALLOWED_DETAILS = new Set([
  'promptId', 'channel', 'status', 'contentType', 'declaredBytes', 'receivedBytes',
  'durationSeconds', 'peak', 'rms', 'muted', 'volume', 'paused', 'readyState',
  'networkState', 'audioContextState', 'errorName', 'engine',
]);

export function inspectPcmWav(input) {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  if (bytes.byteLength < 44) throw new Error('The voice response was too short to be a WAV file.');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const ascii = (offset, length) => String.fromCharCode(...bytes.slice(offset, offset + length));
  if (ascii(0, 4) !== 'RIFF' || ascii(8, 4) !== 'WAVE') throw new Error('The voice response was not a valid WAV file.');

  let offset = 12;
  let format;
  let channels;
  let sampleRate;
  let byteRate;
  let blockAlign;
  let bitsPerSample;
  let dataOffset;
  let dataBytes;
  while (offset + 8 <= bytes.byteLength) {
    const chunk = ascii(offset, 4);
    const size = view.getUint32(offset + 4, true);
    const body = offset + 8;
    if (body + size > bytes.byteLength) throw new Error('The WAV response contained a damaged chunk.');
    if (chunk === 'fmt ' && size >= 16) {
      format = view.getUint16(body, true);
      channels = view.getUint16(body + 2, true);
      sampleRate = view.getUint32(body + 4, true);
      byteRate = view.getUint32(body + 8, true);
      blockAlign = view.getUint16(body + 12, true);
      bitsPerSample = view.getUint16(body + 14, true);
    }
    if (chunk === 'data') { dataOffset = body; dataBytes = size; break; }
    offset = body + size + (size % 2);
  }
  if (format !== 1 || channels !== 1 || sampleRate !== 24000 || bitsPerSample !== 16 || !dataBytes) {
    throw new Error('The voice response was not the required mono 24 kHz 16-bit PCM audio.');
  }
  if (byteRate !== sampleRate * channels * (bitsPerSample / 8) || blockAlign !== channels * (bitsPerSample / 8)) {
    throw new Error('The WAV response contained invalid playback timing fields.');
  }
  if (dataBytes % blockAlign !== 0) throw new Error('The WAV response ended with an incomplete PCM sample.');

  let peak = 0;
  let sumSquares = 0;
  let clipped = 0;
  const sampleCount = Math.floor(dataBytes / 2);
  for (let index = 0; index < sampleCount; index += 1) {
    const normalized = view.getInt16(dataOffset + index * 2, true) / 32768;
    const magnitude = Math.abs(normalized);
    peak = Math.max(peak, magnitude);
    sumSquares += normalized * normalized;
    if (magnitude >= 0.999) clipped += 1;
  }
  const rms = Math.sqrt(sumSquares / sampleCount);
  if (peak < 0.001 || rms < 0.0001) throw new Error('The generated voice was silent or too quiet to use.');
  return {
    format, channels, sampleRate, byteRate, blockAlign, bitsPerSample, dataBytes,
    durationSeconds: dataBytes / byteRate,
    peak,
    rms,
    clippedRatio: clipped / sampleCount,
  };
}

export function friendlyPlaybackError(error) {
  if (error?.name === 'NotAllowedError') return 'Your browser blocked audio. Tap Try Sound Again.';
  if (error?.name === 'NotSupportedError') return 'This device could not read the private voice audio. Tap Try Sound Again.';
  if (/silent|too quiet/i.test(error?.message || '')) return 'The generated voice was too quiet. Ask the director to resend the cue.';
  return error?.message || 'Audio could not play. Tap Try Sound Again.';
}

export function logAudioStage(stage, details = {}, logger = console.info) {
  const safeDetails = {};
  for (const [key, value] of Object.entries(details)) {
    if (ALLOWED_DETAILS.has(key) && value !== undefined) safeDetails[key] = value;
  }
  logger(`[actor-audio] ${stage}`, safeDetails);
}
